import { clsx } from 'clsx';
import {
  type ReactNode,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type PointerEvent,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { EVENTKEY_MAP } from '@repo/lib/constants';
import { debounce } from '@repo/lib/functions';
import { isPopoverOpen } from '@repo/dom/dom';
import type { Alignment, Placement, Offset } from '@repo/dom/position';

import { applyFloatingPosition } from '@repo/ui/FloatingContent/applyFloatingPosition';
import { FloatingPositionProvider } from '@repo/ui/FloatingContent';
import { useRepositionOnContentResize } from '../FloatingContent/useRepositionOnContentResize.js';
import { useTooltip } from './TooltipRoot.js';

import s from './Tooltip.module.css';

export type TooltipProps = ComponentPropsWithoutRef<'div'> & {
  align?: Alignment;
  children?: ReactNode;
  className?: string;
  hasArrow?: boolean;
  offset?: Offset;
  placement?: Placement;
  viewportMargin?: number;
};

export const Tooltip = (props: TooltipProps) => {
  const {
    align = 'center',
    children,
    className,
    hasArrow = false,
    id: idProp,
    offset = 4,
    onMouseEnter,
    onMouseLeave,
    onPointerDown,
    placement = 'top',
    viewportMargin = 4,
    ...attrs
  } = props;
  const {
    cancelClose,
    close,
    id: contextId,
    isOpen,
    setDescriptionId,
    surfacePressedRef,
    tooltipRef,
    triggerRef,
  } = useTooltip();
  const id = idProp ?? contextId;
  const arrowRef = useRef<HTMLSpanElement>(null);

  // The trigger describes itself with whatever is registered here, not with
  // the root's own id, so a root whose surface is conditionally rendered emits
  // no `aria-describedby` at all rather than one naming an element that is not
  // in the document. `InputMessage` and `CodeBlockError` register the same way.
  //
  // Passive rather than layout: a layout effect would flush the trigger's
  // re-render synchronously inside the commit, and a table renders up to a
  // hundred of these. Nothing can observe the gap — a description is resolved
  // when a control is reached, not when it mounts.
  useEffect(() => {
    setDescriptionId(id);
    return () => setDescriptionId(undefined);
  }, [id, setDescriptionId]);

  // The surface holds the tooltip open like the trigger does, as the APG
  // tooltip pattern and WCAG 1.4.13 ask: a pointer crossing from the trigger
  // onto it cancels the close the trigger's leave scheduled. It also breaks the
  // flicker loop a surface positioned over its own trigger would otherwise
  // cause, where covering the trigger closes it and uncovering reopens it.
  const handleMouseEnter = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      onMouseEnter?.(e);
      cancelClose();
    },
    [onMouseEnter, cancelClose],
  );

  const handleMouseLeave = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      onMouseLeave?.(e);
      close();
    },
    [onMouseLeave, close],
  );

  // Released on a pointerup anywhere, because a drag that starts on the
  // surface — on the scrollbar of capped content, say — can end off it.
  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      onPointerDown?.(e);
      surfacePressedRef.current = true;

      const release = new AbortController();
      const handleRelease = () => {
        surfacePressedRef.current = false;
        release.abort();
      };
      window.addEventListener('pointerup', handleRelease, {
        capture: true,
        signal: release.signal,
      });
      window.addEventListener('pointercancel', handleRelease, {
        capture: true,
        signal: release.signal,
      });
    },
    [onPointerDown, surfacePressedRef],
  );

  const handleResize = useCallback(() => {
    close();
  }, [close]);

  const debouncedHandleResize = useMemo(
    () => debounce(handleResize, 150),
    [handleResize],
  );

  const calculateAndApplyPosition = useCallback(() => {
    if (!tooltipRef.current || !triggerRef.current) return;

    applyFloatingPosition({
      floating: tooltipRef.current,
      trigger: triggerRef.current,
      arrow: arrowRef.current,
      placement,
      align,
      offset,
      viewportMargin,
      hasArrow,
    });
  }, [
    tooltipRef,
    triggerRef,
    placement,
    align,
    offset,
    viewportMargin,
    hasArrow,
  ]);

  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    if (isOpen) {
      if (!isPopoverOpen(tooltip)) {
        tooltip.showPopover?.();
      }
      calculateAndApplyPosition();
    } else if (isPopoverOpen(tooltip)) {
      tooltip.hidePopover?.();
    }
  }, [isOpen, tooltipRef, calculateAndApplyPosition]);

  // Content only. A tooltip still does not follow its trigger on scroll
  // (CPMT-5889), and a resize closes it.
  useRepositionOnContentResize(tooltipRef, isOpen, calculateAndApplyPosition);

  // Gated on `isOpen` because a closed tooltip has nothing to reposition, and
  // a page can hold a lot of them — one per row of a table, say — each of
  // which would otherwise hold a window listener and a debounce timer for a
  // popover nobody has opened.
  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      debouncedHandleResize.cancel();
    };
  }, [isOpen, debouncedHandleResize]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === EVENTKEY_MAP.ESCAPE && !e.defaultPrevented) {
        e.preventDefault();
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const floatingPositionValue = useMemo(
    () => ({ arrowRef, hasArrow }),
    [hasArrow],
  );

  return (
    <div
      className={s.tooltipEl}
      data-has-arrow={hasArrow}
      id={id}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDown={handlePointerDown}
      popover="manual"
      role="tooltip"
      ref={tooltipRef}
      {...attrs}
    >
      <div
        style={{ visibility: isOpen ? 'visible' : 'hidden' }}
        className={clsx(s.tooltip, className)}
      >
        <FloatingPositionProvider value={floatingPositionValue}>
          {children}
        </FloatingPositionProvider>
      </div>
    </div>
  );
};
